import { useState, useEffect, Fragment, useMemo } from "react";
import { Disclosure } from "@headlessui/react";
import { ChevronUpDownIcon, XMarkIcon, ArrowPathIcon } from "@heroicons/react/20/solid";
import { Tags } from "../../Ranking/Sidebar/LeftSidebar/RankingSideBarBody";
import { ArrowRightIcon } from "@heroicons/react/24/outline";

interface filterOption {
    value: string | number | boolean | null;
    label: string;
    checked: boolean;
    extraData?: number;
    isSubtag: boolean;
    parentTag?: string;
};

interface ITagFilter{
    id: string;
    name: string;
    options: filterOption[];
    filteredOptions: filterOption[];
};

const initialTagFilter: ITagFilter = {
    id: "tagFilter",
    name: "Tag",
    options: [],
    filteredOptions: [],
};

interface Props {
    applyFilters: (filter: any) => void;
    tab: string;
    refreshTags: () => void;
    tags: Tags[];
    view: string;
}

const TagFilter = ({
    applyFilters,
    tab,
    refreshTags,
    tags,
    view
}: Props) => {
    const [tagFilter, setTagFilter] =   useState<ITagFilter>(initialTagFilter);
    const [tagSearch, setTagSearch] = useState(""); 
     
    const filteredOptions = useMemo(() => {
      if (!tagFilter.options.length) return [];
      return tagFilter.options.filter((t) =>
        t.label.toLowerCase().startsWith(tagSearch.toLowerCase())
      );
    }, [tagFilter.options, tagSearch]);

    const setFilterForTag = () => {
      let updatedFilterOption = tags?.map((c:any) => ({
        checked: false,
        value: c.id,
        label: c.name,
        extraData: c.count ?? 0,
        isSubtag: false
      }));
      
      let updatedSubtagOptions = [];
      tags?.forEach((c: any) => {
        updatedSubtagOptions = c.subtags?.map((subtag: any) => ({
          checked: false,
          value: subtag.tagSubtagId,
          label: subtag.name,
          extraData: subtag.count ?? 0,
          isSubtag: true,
          parentTag: c.name
        }));
        if(updatedSubtagOptions && updatedSubtagOptions.length !== 0) {
          updatedFilterOption = [...updatedFilterOption, ...updatedSubtagOptions];
        }
        
      }) 
      
      let typeFilter = {
        ...initialTagFilter,
        options: updatedFilterOption
      };

      setTagFilter(typeFilter);
    }
    
    useEffect(() => {
      setFilterForTag();
    }, [ tags, tab, tagSearch ]);

    useEffect(() => {
      if (!tagFilter.options.length) return;
      const timerId = setTimeout(() => {
        setTagFilter((prev) => (
          {
            ...prev,
            filteredOptions
          }
        ))
      }, 250);

      return () => {
        clearTimeout(timerId);
      }
    }, [filteredOptions]);

    const handleTagFilterChange = async (changedOption: filterOption) => {

        const updatedOptions = tagFilter.options.map((option) => {
          if (option.value === changedOption.value) {
            return { ...option, checked: !option.checked };
          }
          return option;
        });
        const updatedFilteredOptions = tagFilter.filteredOptions.map((fo) => {
          if (fo.value === changedOption.value) {
            return { ...fo, checked: !fo.checked };
          }
          return fo;
        });
    
        setTagFilter((prev) => ({
          ...prev,
          options: updatedOptions,
          filteredOptions: updatedFilteredOptions,
        }));

        applyFilters([{
          FilterType: "tag",
          Tags: updatedOptions.filter((uo) => uo.checked && !uo.isSubtag).map((uo) => uo.value),
          Subtags: updatedOptions.filter((uo) => uo.checked && uo.isSubtag).map((uo) => uo.value) 
        }]);
        
    };

  return (
    <>
        <Disclosure
        as="div"
        className={`border-b border-gray-200 py-4 dark:border-gray-500 ${view === "quarantine" ? 'px-3' : ''} `}
        >
        {({ open, close }) => (
          <div>
            <div className="relative">
              <h3 className="-my-4 flow-root">
                <Disclosure.Button className="flex w-full items-center py-3 justify-between text-sm text-gray-400 hover:text-gray-500">
                  <span className="font-medium text-gray-900 dark:text-gray-300">
                    {tagFilter.name}
                  </span>
                  <span className="ml-3 flex items-center">
                    <div className="relative w-full cursor-default overflow-hidden rounded-md bg-white text-left ring-1 ring-gray-300 sm:text-sm dark:ring-gray-500">
                      <input
                        type="text"
                        value={tagSearch}
                        placeholder="Search tags"
                        className="w-full border-none py-1 pl-3 pr-10 text-sm text-gray-900 focus:ring-0 placeholder:text-gray-400 dark:text-gray-300 dark:bg-darkbg"
                        onChange={(event) =>
                          setTagSearch(event.target.value)
                        }
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon
                          className="h-5 w-5 text-gray-400 dark:text-gray-400"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  </span>
                </Disclosure.Button>
              </h3>

              <Disclosure.Panel
                className={`dark:bg-darkbglight dark:border-gray-500 shadow-sm pl-1 py-1 h-fit absolute bg-white rounded-md z-50 border border-gray-200 bottom-[2.6rem] right-[1rem]`}
              >
                <div className="max-h-[10.5rem] min-w-[14.8rem] px-2 py-1 overflow-auto thin-scroll bg-transparent z-10">
                  <div className="space-y-2">
                    <div className="pb-2 flex justify-between items-center">
                      <p className="text-entntblue text-sm font-medium">Tags</p>
                      <button onClick={refreshTags}>
                        <ArrowPathIcon className="h-4 w-4 text-gray-600 dark:text-gray-300 "/>
                      </button>
                    </div>
                    {tagFilter.filteredOptions.map((option:any, optionIdx) => {
                      return (
                        <div key={optionIdx} className="flex justify-between gap-3 items-center border-b-2 dark:border-gray-400 pb-2">
                          <div  className="flex items-center">
                            {
                              option.extraData ? (<input
                                id={`tag-filter-${optionIdx}`}
                                type={"checkbox"}
                                onChange={() => {
                                  handleTagFilterChange(option);
                                }}
                                checked={option.checked}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />) : <></>
                            }

                            <label
                              id={`tag-filter-${optionIdx}`}
                              className="ml-3 text-sm text-gray-600 flex justify-between items-center w-full pr-2 gap-2 dark:text-gray-400"
                            >
                              {
                                option.parentTag && 
                                <>
                                  <span>{option.parentTag} </span>
                                  <ArrowRightIcon className="h-3 w-3"/>
                                </>
                                
                              }
                              <span> {option.label}</span>
                            </label>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </Disclosure.Panel>
            </div>
            {open && (
              <div
                className="fixed inset-0 z-20"
                onClick={() => {
                  close();
                }}
              ></div>
            )}
            <div className="z-10 text-xs flex flex-wrap box-border mt-2">
              {tagFilter.options.map((o, index) =>
                o.checked ? (
                  <span
                    key={o.label + index}
                    className="flex items-center bg-blue-100 text-entntblue py-1 px-2 mr-2 mt-2 rounded-full relative"
                  >
                    {o.label}
                    <XMarkIcon
                      onClick={() => {
                        handleTagFilterChange(o);
                      }}
                      className="h-4 w-4 cursor-pointer text-gray-500 ml-1 hover:text-gray-600"
                    />
                  </span>
                ) : (
                  <Fragment key={index}></Fragment>
                )
              )}
            </div>
          </div>
        )}
      </Disclosure>
     
    </>
  )
}

export default TagFilter
